import canvasManager from "slotworks/graphics/canvas/canvas-manager";
import graphicsManager from "slotworks/graphics/graphics-manager";
import Layers from "slotworks/graphics/layers/layers";
import slotDefinition from "slotworks/model/slot-definition";
import { LLCustomLayers } from "names";
import BoxDimSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/box-dim-symbol-behaviour";
import betModel from "slotworks/model/bets/bet-model";
import reelsetModifier from "utils/reelset-modifier";
import deviceInfo from "slotworks/utils/device-info";

class BonusSymbolSetup {

    private anticipationSymbols = new Map<number, string[]>();

    private labels = [
        "GRAND", "MAJOR", "MINOR", "MINI", "50", "100", "150",
        "200", "250", "300", "400", "450", "500", "600", "750",
        "1000", "1250", "1500", "2000", "2500", "2250",
        "3000", "3750", "5000", "7500", "75000", "10000", "12500",
        "15000", "22500", "25000", "37500", "50000"
    ];

    public getSymbolIds() {
        return this.labels.map((label) => `TT_${label}`);
    }

    public setupDefinitions() {

        const mcSymbolDefinition = slotDefinition.symbolDefinitions.find((symbol) => symbol.id === "TT");

        this.labels.forEach((label) => {
            const symbolId = "TT_" + label;
            const bonusSymbolDefinition = mcSymbolDefinition.clone();
            bonusSymbolDefinition.id = symbolId;

            slotDefinition.symbolDefinitions.push(bonusSymbolDefinition);
        });
    }

    public setupGraphics() {

        const layer = Layers.Custom.get(LLCustomLayers.MONEY_CHARGE_BONUS_SYMBOLS);

        // Put the text layers are on screen to manipulate.
        layer.defaultScene().execute();

        this.labels.forEach((label) => {

            const symbolId = "TT_" + label;
            let labelTexture;

            // Some bonus symbols have predefined graphics.
            if (graphicsManager.hasTexture(symbolId)) {
                labelTexture = graphicsManager.getTexture(symbolId);
            } else {
                labelTexture = this.createBonusTexture(label);
                graphicsManager.addTexture(symbolId, labelTexture);
            }

            const curtainTextureName = symbolId + "_b";

            if (!graphicsManager.hasTexture(curtainTextureName)) {
                const curtainedTexture = this.createCurtainedBonusTexture(label);
                graphicsManager.addTexture(curtainTextureName, curtainedTexture);
            }

            const smallTexture = this.createSmallBonusTexture(symbolId);
            graphicsManager.addTexture(symbolId + "_small", smallTexture);
        });

        this.initAnticipationSymbols();
    }

    public drawAllBonusSymbols() {
        let x = 100;
        let y = 100;

        const layer = Layers.Custom.get(LLCustomLayers.LOGOS);
        const position = Layers.MatrixContent.getPosition("static");

        const labels = ["GRAND", "MAJOR", "MINOR", "MINI", "50"];

        labels.forEach((label) => {
            const bonusSymbol = graphicsManager.createSprite(`TT_${label}`);
            bonusSymbol.x = x;
            bonusSymbol.y = y;

            bonusSymbol.width = position.width;
            bonusSymbol.height = position.height;

            layer.container.addChild(bonusSymbol, bonusSymbol);
            x += bonusSymbol.width;

            if (x >= deviceInfo.getScreenWidth()) {
                x = 0;
                y += bonusSymbol.height;
            }
        });
    }

    public getAnticipationSymbols() {
        return this.anticipationSymbols.get(betModel.getCreditPerLine());
    }

    public setAnticipationSymbols(stakes: number[], anticipationSymbols: string[][]) {
        stakes.forEach((stake, index) => {
            this.anticipationSymbols.set(stake, anticipationSymbols[index]);
        });
    }

    private initAnticipationSymbols() {
        reelsetModifier.updateMCSymbolValues("base");
    }

    private createBonusTexture(label: string) {

        const symbolText = this.getTextElementForLabel(label).clone();
        const bonusSymbol = graphicsManager.createSprite("TT");

        // Positions aren't available this early in the game.
        symbolText.scale.y = 1.5;

        bonusSymbol.width = 184;
        bonusSymbol.height = 183;

        symbolText.x = bonusSymbol.width / 2;
        symbolText.y = bonusSymbol.height / 2;

        symbolText.updateText();

        const labelTexture = PIXI.RenderTexture.create(bonusSymbol.width, bonusSymbol.height);

        const renderer = canvasManager.renderer;
        renderer.render(bonusSymbol, labelTexture, false);
        renderer.render(symbolText, labelTexture, false);

        return labelTexture;
    }

    private createSmallBonusTexture(label: string) {
        const sprite = graphicsManager.createSprite(label);
        sprite.scale.x *= 0.1;
        sprite.scale.y *= 0.1;

        const labelTexture = PIXI.RenderTexture.create(sprite.width, sprite.height);
        const renderer = canvasManager.renderer;
        renderer.render(sprite, labelTexture, false);
        return labelTexture;
    }

    private createCurtainedBonusTexture(label: string) {
        const labelTexture = graphicsManager.createSprite("TT_" + label);
        const dimTexture = PIXI.RenderTexture.create(labelTexture.width, labelTexture.height);
        const curtain = this.getCurtainGraphic();
        const renderer = canvasManager.renderer;

        renderer.render(labelTexture, dimTexture, false);
        renderer.render(curtain, dimTexture, false);

        return dimTexture;
    }

    private getCurtainGraphic() {
        const curtain = new PIXI.Graphics();

        const bonusSymbol = graphicsManager.createSprite("TT");

        curtain.beginFill(BoxDimSymbolBehaviour.color, BoxDimSymbolBehaviour.opacity);
        curtain.drawRect(0, 0, bonusSymbol.width, bonusSymbol.height);
        curtain.endFill();

        return curtain;
    }

    private getTextElementForLabel(label: string) {
        const layer = Layers.Custom.get(LLCustomLayers.MONEY_CHARGE_BONUS_SYMBOLS);

        switch (label) {
            case "GRAND":
                return layer.getText("MC_GRAND");
            case "MAJOR":
                return layer.getText("MC_MAJOR");
            case "MINOR":
                return layer.getText("MC_MINOR");
            case "MINI":
                return layer.getText("MC_MINI");
            default:
                return null;
        }
    }
}

export default new BonusSymbolSetup();
